import { CardContent, CardHeader, Stack } from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import MaterialTypography from "../../../components/MaterialTypography";
import { useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { useEffect, useState } from "react";
import { deleteData, fetchData, postData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useSnackbar } from "../../../context/popup";
import UploadFile from "../../Utils/UploadFile";
import ExternalApiRegistryFilterDialog from "./components/ExternalApiRegistryFilterDialog";
import { useLoading } from "../../../components/Loading";
import { FilterAlt } from "@mui/icons-material";

const ExternalApiRegistryList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.ExternalApiRegistryStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.ExternalApiRegistryStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.ExternalApiRegistryStore?.totalPages
  );

  const [isRefresh, setIsRefresh] = useState(false);
  const [apiRegistry, setApiRegistry] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    iRegistryId: "",
    gProductId: "",
    apiStatusId: "",
  });

  useEffect(() => {
    fetchExternalApiRegistryList();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //List API
  const fetchExternalApiRegistryList = async () => {
    setCircleLoading(true);
    try {
      let url = `/api-engine/api-registry?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;

      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      if (filterData.iRegistryId !== "") {
        url += `&iRegistryId=${filterData.iRegistryId}`;
      }

      if (filterData.gProductId !== "") {
        url += `&gProductId=${filterData.gProductId}`;
      }

      if (filterData.apiStatusId !== "") {
        url += `&apiStatusId=${filterData.apiStatusId}`;
      }

      const response = await fetchData(url);

      if (response?.success) {
        setApiRegistry(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        //throw error
        showSnackbar("Error Fetching External Api Registry List", "error");
      }
    } catch (error) {
      console.error("Error Fetching External Api Registry:", error);
      showSnackbar(error?.response?.data?.message || "Error Fetching External Api Registry List", "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(`/api-engine/schema`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (result?.success) {
        setUploadSuccess(true);
        showSnackbar(result?.message, "success");
        setOpenUploadFileDialog(false);
        setClearFileOnSuccess(true);
        fetchExternalApiRegistryList();
      } else {
        setUploadSuccess(false);
        showSnackbar("Faiiled to upload file", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      console.error("Upload error:", error || errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setFileUploading(false);
    }
  };

  //delete api
  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/api-engine/api-registry-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar("External Api Registry deleted successfully", "success");
      } else {
        showSnackbar("Failed to delete External Api Registry", "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message || "Error Deleting External Api Registry", "error");
    }
  };

  //Table Mapping
  const ExternalApiRegistryListTable = {
    columns: [
      {
        key: "sno",
        label: "S.No.",
        renderCell: (row) => row.sno,
      },
      {
        key: "name",
        label: "API Name",
        renderCell: (row) => row.name,
      },
      {
        key: "code",
        label: "Code",
        renderCell: (row) => row.code,
      },
      {
        key: "description",
        label: "Description",
        renderCell: (row) => row.description || "-",
      },
      {
        key: "gInsurerId",
        label: "General Insurer",
        renderCell: (row) => row.gInsurerId,
      },
      {
        key: "gProductId",
        label: "General Product",
        renderCell: (row) => row.gProductId,
      },
      {
        key: "apiStatusId",
        label: "API Status",
        renderCell: (row) => row.apiStatusId,
      },
      {
        key: "isActive",
        label: "Status",
        renderCell: (row) => (
          <span
            style={{
              color: row.isActive ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {row.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],

    rows: apiRegistry?.map((item, index) => ({
      id: item.id,
      sno: index + 1,

      name: item.name,
      code: item.code,
      description: item.description ?? "-",

      // If backend later sends label object, this will still work
      gProductId: item.productDetails?.keyName ?? item.gProductId ?? "-",
      apiStatusId: item.apiStatusDetails?.keyName ?? item.apiStatusId ?? "-",
      gInsurerId: item.insurerDetails?.gInsurerId ?? item.apiStatusId ?? "-",

      isActive: item.isActive,
    })),
  };

  const handleAddNewApiRegistry = () => {
    navigate(
      `/api-integration-engine/external-api-registry/external-api-registry-details`
    );
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,

    onEdit: (row) => {
      navigate(
        `/api-integration-engine/external-api-registry/external-api-registry-details?pageType=edit&id=${row.id}`
      );
    },
    onView: (row) => {
      navigate(
        `/api-integration-engine/external-api-registry/external-api-registry-details?pageType=view&id=${row.id}`
      );
    },
    onDelete: (row) => handleDelete(row),
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Api Registry List
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Api Registry around the world.
              </MaterialTypography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>

              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewApiRegistry()}
              >
               Api Registry
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <Table
          columns={ExternalApiRegistryListTable?.columns}
          data={ExternalApiRegistryListTable?.rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No Api Registry found"
          handleSearch={handleSearch}
        />
      </CardContent>
      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />
      <ExternalApiRegistryFilterDialog
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchExternalApiRegistryList(); // call API after filter applied
        }}
      />
    </DashboardLayout>
  );
};

export default ExternalApiRegistryList;
